import React, { FC } from 'react';
import { RootState, useSelector } from 'src/store';
import { changeEnterHTML } from 'src/utils/changeEnter';
import { getFontURL } from 'src/utils/getFontUrl';
import { trackingList } from 'src/utils/valueHTML';

const SimpleIconModalHTML: FC = () => {
  const bg = useSelector((state: RootState) => state.imageUpload.bg);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  const deeplink = useSelector(
    (state: RootState) => state.imageUpload.deeplink
  );
  const radius = useSelector((state: RootState) => state.imageUpload.radius);

  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);

  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);

  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const tracking = useSelector((state: RootState) => state.trackings.tracking);
  const button1 = useSelector((state: RootState) => state.trackings.button1);
  const button2 = useSelector((state: RootState) => state.trackings.button2);
  const btnAgain = useSelector((state: RootState) => state.buttons.btnAgain);
  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.7;

  const titleFontURL = getFontURL(fontFamily?.title);
  const subTitleFontURL = getFontURL(fontFamily?.sub_title);
  const btnFontURL = getFontURL(btnFontFamily?.button);
  const btnFontSubURL = getFontURL(btnFontFamily?.sub_button);
  const btnFontNeverShowURL = getFontURL(btnFontFamily?.never_show_button);

  const style = `

    @font-face {
    font-family: 'titleFont';
    src: url(${titleFontURL});
    }

    @font-face {
    font-family: 'subTitleFont';
    src: url(${subTitleFontURL});
    }

    @font-face {
      font-family: 'btnFont';
      src: url(${btnFontURL});
      }
  
    @font-face {
      font-family: 'btnSubFont';
      src: url(${btnFontSubURL});
      }
  
    @font-face {
      font-family: 'btnNeverShowFont';
      src: url(${btnFontNeverShowURL});
      }
  

    /* reset */
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      list-style: none;
      text-decoration: none;
      
    }
    html,
    body {
      height: 100%;
      overflow: hidden;
      font-size: 16px;   
    }
    main {
      height: 100%;
      position: relative;
    }
    a {
      color: #000000;
      cursor: pointer;
    }
    button {
      background: none;
      border: none;
    }

    /* custom */
    .root {
      width: 100%;
      height: 100%;
      
      position: relative;
      background: none;
      overflow: hidden;
    }
    .background {
      width: 100%;
      height: 100%;

      position: absolute;
      top: 0;
      background: rgba(0, 0, 0, ${displayValue});
      padding: 0 10%;
    }
    .center {
      width: 80%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      display: flex;
      flex-direction: column;
      
    }
    .modal_wrapper {
      width: 100%;
      padding: 25.92px 20px 20px 20px;

      display: flex;
      flex-direction: column;
      
      
      border-radius: ${radius?.background}px;
      ${
        bg === 'image'
          ? `background-image: url(${bgImage?.background});`
          : `background: ${bgColor?.background};`
      };

      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    }

    .icon {
      text-align: center;
    }
    .icon img {
      min-width: 70px;
      min-height: 70px;
      max-width: 140px;
      max-height: 140px;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;

      font-size: 0;
      border: none;
      background-color: transparent;
    }

    .title {
      display: flex;
      align-items: center; 
      margin-bottom: 7px;
    }
    .title_inner {
      width: 100%;
      height: auto;      
      line-height: 1.4;
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;

      text-align: ${textAlign?.title};
      color: ${color?.title};
      font-size: ${fontSize?.title}rem;
      font-weight: ${fontWeight?.title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.title
        )
          ? fontFamily.title
          : 'titleFont'
      };
    }
    .sub_title {      
      display: flex;
      align-items: center;
    }
    .sub_title_inner {
      width: 100%;
      height: auto;
      max-height: 48px;

      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-height: 1.54;
      text-align: ${textAlign?.sub_title};
      color: ${color?.sub_title};
      font-size: ${fontSize?.sub_title}rem;
      font-weight: ${fontWeight?.sub_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.sub_title
        )
          ? fontFamily.sub_title
          : 'subTitleFont'
      }
    }

    .btn_wrapper {
      width: 100%;            
    }
    .btn_wrapper button {
      width: 100%;
      height: 48px;
      outline: 0;
      border: 0;
      padding: 0 16px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      font-size: ${btnFontSize?.button}rem;
      font-weight: ${btnFontWeight?.button};
      color: ${btnColor?.button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.button
        )
          ? btnFontFamily?.button
          : 'btnFont'
      };

      border-radius: ${radius?.button}px;
      background-color:rgba(${btnBgColorR?.button},${btnBgColorG?.button}, ${
    btnBgColorB?.button
  }, ${btnBgColorA?.button});
    }

    .bottom_wrapper {
      margin-top: 11px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-left: 4px;
    }
    .bottom_wrapper .style01 {
      width: 100%;
      display: flex;
      align-items: center;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      color: ${btnColor?.never_show_button};
      font-weight: ${btnFontWeight?.never_show_button};
      font-size: ${btnFontSize?.never_show_button}rem;
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.never_show_button
        )
          ? btnFontFamily.never_show_button
          : 'btnNeverShowFont'
      };
      
      background-color:rgba(
      ${btnBgColorR?.never_show_button},${btnBgColorG?.never_show_button},
      ${btnBgColorB?.never_show_button}, ${btnBgColorA?.never_show_button});
    }
    .bottom_wrapper .style02 {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      color: ${btnColor?.sub_button};
      font-weight: ${btnFontWeight?.sub_button};
      font-size: ${btnFontSize?.sub_button}rem;
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.sub_button
        )
          ? btnFontFamily.sub_button
          : 'btnSubFont'
      };
      
      background-color:rgba(
        ${btnBgColorR?.sub_button},${btnBgColorG?.sub_button}, 
        ${btnBgColorB?.sub_button},${btnBgColorA?.sub_button});
    }
   

		@media screen and (min-width: 600px) and (max-width: 1024px) {
      .center {
        width: 50%;
      }
      .modal_wrapper {
        padding: 32px;
      }    
    }
  `;
  const deeplink_list = JSON.stringify([deeplink?.background]);

  const html = `
    <div class="root" id="test">
      <div class="background" onclick="appboyBridge.logClick('background'); appboyBridge.closeMessage();"></div>
      <div class="center">
        <div class="modal_wrapper">
          <div class="icon">
            <img
              src="${bgImage?.logo}"
              alt="icon"
              decoding="async"
              loading="lazy"
            />
          </div>
          <div style="margin: 16px 0px;">
            <div class="title">
              <div class="title_inner">${changeEnterHTML(text?.title)}</div>
            </div>
            <div class="sub_title">
              <div class="sub_title_inner">${changeEnterHTML(
                text?.sub_title
              )}</div>
            </div>
          </div>
          <div class="btn_wrapper">
            <button class="btn_inner">
              ${btnText?.button}
            </button>
          </div>
        </div>
        <div class="bottom_wrapper">
          <button class="style01">
            ${btnText?.never_show_button}
          </button>
          <div style="display: flex; justify-content: center; align-items: center; width: 100%;">
           <button class="style02">
              ${btnText?.sub_button}
            </button>
            <span class="material-icons" style="font-size: ${btnFontSize?.sub_button +
              0.35}rem; color:${btnColor?.sub_button};">close</span>
          </div>
        </div>
      </div>
      <div id="deeplink" style="display:none;">
            ${deeplink_list}
      </div>
    </div>
  `;

  let tracking_list = '[';
  for (let i = 0; i < tracking?.button?.length; i++) {
    tracking_list += `["${tracking?.button?.[i]}", "${tracking?.left_button?.[i]}", "${tracking?.right_button?.[i]}"],`;
  }
  tracking_list += ']';

  const tracking_button_list = trackingList(
    'SIMPLE ICON MODAL',
    button1,
    button2
  );

  const js = `
  document.addEventListener("DOMContentLoaded", function(){      
    const content = document.getElementById("deeplink").textContent;
    const link = content.replace(/\\n|\\t/g, "").trim();
    const deeplink = JSON.parse(link);

    let tracking = ${tracking_list};
    let button1 = ${tracking_button_list[0]};
    let button2 = ${tracking_button_list[1]};
    $('.btn_wrapper').click(function() {
      try {
        appboyBridge.logClick(tracking[0][0]);
        if (button1[0].indexOf(0) !== -1) {
          appboyBridge.logClick('0');
        }
        if (button2[0].indexOf(0) !== -1) {
          appboyBridge.logClick('1');
        }
      } catch (err) {}
      window.location = deeplink[0];
    });
    $('.style01').click(function() {
      try {
        if ("${btnAgain?.type}" === "attribute") {
          appboyBridge.getUser().setCustomUserAttribute("${btnAgain?.never_show_button}", new Date());
        }
        if ("${btnAgain?.type}" === "event") {
          appboyBridge.logCustomEvent("${btnAgain?.never_show_button}");
        }
      } catch (err) {}
      try {
        appboyBridge.logClick(tracking[0][1]);
        if (button1[2].indexOf(0) !== -1) {
          appboyBridge.logClick('0');
        }
        if (button2[2].indexOf(0) !== -1) {
          appboyBridge.logClick('1');
        }
      } catch (err) {}
      appboyBridge.closeMessage();
    });
    $('.style02').click(function() {
      try {
        appboyBridge.logClick(tracking[0][2]);
        if (button1[1].indexOf(0) !== -1) {
          appboyBridge.logClick('0');
        }
        if (button2[1].indexOf(0) !== -1) {
          appboyBridge.logClick('1');
        }
      } catch (err) {}
      appboyBridge.closeMessage();
    });
  });
  `;

  return (
    <>
      <head>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta
          httpEquiv="Cache-Control"
          content="no-cache, no-store, must-revalidate"
        />
        <meta httpEquiv="Expires" content="0" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="stylesheet"
          as="style"
          href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdn.jsdelivr.net/npm/normalize.css@8.0.1/normalize.css"
        />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/icon?family=Material+Icons"
        />
        <style dangerouslySetInnerHTML={{ __html: style }} />
        <script
          dangerouslySetInnerHTML={{
            __html: `               
         (function () {
          function adjustFontSize() {
            var actualSize = Math.round(parseFloat(window.getComputedStyle(document.documentElement).fontSize));
            if (actualSize !== 16) {              
              var newFontSize = (16 * 16) / actualSize;
              document.documentElement.style.fontSize = newFontSize + "px";                            
            }
          }              

          // adjust font size before body load
              adjustFontSize();
              })();                           
          `
          }}
        />
      </head>
      <body dangerouslySetInnerHTML={{ __html: html }} />
      <script dangerouslySetInnerHTML={{ __html: js }} />
    </>
  );
};
export default SimpleIconModalHTML;
